import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import api from '../../../../api';
import { AppAsyncSelect, AppSelect } from '../../../../components/common/select';
import CurrencyInput from 'react-currency-input-field';
import Alert from '../../../../components/Alert/Alert';
import useModalEffect from '../../../../hooks/useModalEffect';

// ✅ MULTI-CURRENCY: Helper function untuk mendapatkan currency prefix
const getCurrencyPrefix = (currencyCode) => {
  const getCurrencySymbol = (code) => {
    switch (code) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'JPY': return '¥';
      case 'GBP': return '£';
      case 'SGD': return 'S$';
      case 'AUD': return 'A$';
      case 'IDR': return 'Rp';
      default: return 'Rp';
    }
  };
  return getCurrencySymbol(currencyCode) + ' ';
};

const PaymentForm = memo(({ isOpen, onClose, formData: initialFormData, formErrors, handleInputChange: parentHandleInputChange, handleFormSubmit: parentHandleFormSubmit, isEditMode, editingPayment, refreshPayments, disableClientSelect = false, disableInvoiceSelect = false, currencyCode = 'IDR', presetClient = null }) => {
  // ✅ PERFORMANCE: Use React Hook Form for optimized form state management
  const { control, handleSubmit, reset, watch, setValue, setError, getValues, formState: { errors } } = useForm({
    defaultValues: {
      client: '',
      invoice: '',
      paymentMethod: '',
      paymentDate: new Date().toISOString().split('T')[0],
      amount: '',
      note: '',
      documents: []
    },
    mode: 'onBlur', // Only validate on blur, not on every keystroke
    shouldUnregister: false // Keep form values even when fields unmount
  });

  // Watch form values for currency updates (doesn't cause re-renders)
  const watchedClient = watch('client');
  const watchedInvoice = watch('invoice');
  
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState(currencyCode); // ✅ MULTI-CURRENCY: Track current currency
  const [documents, setDocuments] = useState([]); // Separate state for documents

  useModalEffect(isOpen);

  // Inisialisasi payment method options
  const paymentMethods = [
    { id: 'Bank Transfer', name: 'Bank Transfer' },
    { id: 'Cash', name: 'Cash' },
    { id: 'Credit Card', name: 'Credit Card' },
    { id: 'Debit Card', name: 'Debit Card' },
    { id: 'E-Wallet', name: 'E-Wallet' }
  ];

  // Set payment method options saat component mount
  useEffect(() => {
    setPaymentMethodOptions(paymentMethods);
  }, []);

  // clientInitialValue: pre-populate AppAsyncSelect in edit mode so the saved
  // company name appears immediately without waiting for the user to search.
  const clientInitialValue = isEditMode && editingPayment
    ? {
        value: String(editingPayment.clientId || editingPayment.client || ''),
        label: editingPayment.clientName || ''
      }
    // Create mode with a client chosen by the parent (company detail page).
    // The select loads its options on demand, so without an explicit label
    // the preselected client renders blank.
    : presetClient && presetClient.value
      ? { value: String(presetClient.value), label: presetClient.label || '' }
      : null;

  // ✅ MULTI-CURRENCY: Helper function to get locale based on currency code
  const getLocaleForCurrency = (currencyCode) => {
    switch (currencyCode) {
      case 'USD': return 'en-US';
      case 'EUR': return 'de-DE'; // or 'fr-FR', 'it-IT' depending on preference
      case 'GBP': return 'en-GB';
      case 'JPY': return 'ja-JP';
      case 'SGD': return 'en-SG';
      case 'AUD': return 'en-AU';
      case 'IDR': return 'id-ID';
      default: return 'id-ID'; // Default to Indonesian locale
    }
  };

  // ✅ PERFORMANCE: Memoize fetch functions to prevent recreation
  const fetchInvoiceOptions = useCallback(async (clientId) => {
    if (!clientId) {
        setInvoiceOptions([]);
        return;
    }
    
    setIsLoadingInvoices(true);
    try {
        const res = await api.get(`/crm/invoices/company/${clientId}`);
        
        const formatted = res.data.map(invoice => {
            const currencyCode = invoice.displayCurrencyCode || invoice.currencyCode || 'IDR';
            const locale = getLocaleForCurrency(currencyCode);
            
            return {
                value: invoice.id,
                label: `${invoice.invoiceNumber} - ${new Intl.NumberFormat(locale, { 
                    style: 'currency', 
                    currency: currencyCode
                }).format(invoice.totalInvoiced || 0)}`,
                name: invoice.invoiceNumber,
                balanceDue: invoice.balance || 0,
                status: invoice.status,
                total: invoice.totalInvoiced || 0,
                currencyCode: currencyCode
            };
        });
        setInvoiceOptions(formatted);
    } catch (error) {
        setInvoiceOptions([]);
    } finally {
        setIsLoadingInvoices(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // ✅ MULTI-CURRENCY: Reset currency to default when modal opens
      setCurrentCurrencyCode(currencyCode);
    }
  // eslint-disable-next-line
  }, [isOpen, currencyCode]);

  // ✅ PERFORMANCE: Handle edit mode - reset form with editing data
  useEffect(() => {
    if (isOpen && isEditMode && editingPayment) {
      const transformedData = {
        client: editingPayment.clientId || editingPayment.client || '',
        invoice: editingPayment.invoiceId || editingPayment.invoice || '',
        paymentMethod: editingPayment.paymentMethod || '',
        paymentDate: editingPayment.paymentDate || new Date().toISOString().split('T')[0],
        amount: editingPayment.amount ? editingPayment.amount.toString() : '',
        note: editingPayment.note || '',
        documents: []
      };
      
      reset(transformedData);
      
      // Set uploaded files if editing
      if (editingPayment?.documents && editingPayment.documents.length > 0) {
        const existingFiles = editingPayment.documents.map(doc => ({
          name: doc.fileName || doc.name || 'Document',
          size: 0,
          type: doc.fileType || 'application/pdf',
          url: doc.url || `/api/crm/payments/download/${editingPayment.id}`,
          isExisting: true
        }));
        setDocuments(existingFiles);
      } else if (editingPayment?.fileName) {
        const existingFile = {
          name: editingPayment.fileName,
          size: 0,
          type: 'application/pdf',
          url: `/api/crm/payments/download/${editingPayment.id}`,
          isExisting: true
        };
        setDocuments([existingFile]);
      } else {
        setDocuments([]);
      }
      
      // Fetch invoice options if we have client
      if (transformedData.client) {
        fetchInvoiceOptions(transformedData.client);
      }
      
      // ✅ MULTI-CURRENCY: If invoice exists, fetch its currency (takes priority)
      if (transformedData.invoice) {
        api.get(`/crm/invoices/${transformedData.invoice}`)
          .then(invoiceRes => {
            const invoiceCurrency = invoiceRes.data?.displayCurrencyCode || 
                                   invoiceRes.data?.currencyCode || 
                                   'IDR';
            setCurrentCurrencyCode(invoiceCurrency);
          })
          .catch(() => {
            // Keep current currency if fetch fails
          });
      }
    }
  }, [isOpen, isEditMode, editingPayment?.id, reset, fetchInvoiceOptions]);

  // ✅ MULTI-CURRENCY: Update currency when prop changes
  useEffect(() => {
    setCurrentCurrencyCode(currencyCode);
  }, [currencyCode]);

  // ✅ MULTI-CURRENCY: Update currency when invoice is selected (priority over company currency)
  // ✅ PERFORMANCE: Use watchedInvoice from react-hook-form (doesn't cause re-renders)
  useEffect(() => {
    if (watchedInvoice && !disableInvoiceSelect) {
      // Try to get currency from invoice options first (faster, already loaded)
      if (invoiceOptions.length > 0) {
        const selectedInvoice = invoiceOptions.find(inv => inv.value === watchedInvoice);
        if (selectedInvoice?.currencyCode) {
          setCurrentCurrencyCode(selectedInvoice.currencyCode);
          return;
        }
      }
      
      // Fallback: Fetch invoice details to get currency if not in options
      const fetchInvoiceCurrency = async () => {
        try {
          const invoiceRes = await api.get(`/crm/invoices/${watchedInvoice}`);
          const invoiceCurrency = invoiceRes.data?.displayCurrencyCode || 
                                 invoiceRes.data?.currencyCode || 
                                 currencyCode;
          setCurrentCurrencyCode(invoiceCurrency);
        } catch (error) {
          // Keep current currency if fetch fails
        }
      };
      
      fetchInvoiceCurrency();
    }
  }, [watchedInvoice, invoiceOptions, disableInvoiceSelect, currencyCode]);

  // ✅ NEW: Handle initial form data from parent (for create mode with pre-filled data)
  // Only run when modal opens for the first time, not on every input change
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (isOpen && !isEditMode) {
      // Only initialize once when modal opens
      if (!hasInitialized.current && initialFormData) {
        // Only update if we have meaningful data (client or invoice)
        if (initialFormData.client || initialFormData.invoice) {
          reset({
            client: initialFormData.client || '',
            invoice: initialFormData.invoice || '',
            paymentMethod: initialFormData.paymentMethod || '',
            paymentDate: initialFormData.paymentDate || new Date().toISOString().split('T')[0],
            amount: initialFormData.amount || '',
            note: initialFormData.note || '',
            documents: []
          });
          
          // Fetch invoice options if client is provided
          if (initialFormData.client) {
            fetchInvoiceOptions(initialFormData.client);
          }
          
          // ✅ MULTI-CURRENCY: If invoice is pre-filled, fetch its currency immediately
          if (initialFormData.invoice) {
            api.get(`/crm/invoices/${initialFormData.invoice}`)
              .then(invoiceRes => {
                const invoiceCurrency = invoiceRes.data?.displayCurrencyCode || 
                                       invoiceRes.data?.currencyCode || 
                                       'IDR';
                setCurrentCurrencyCode(invoiceCurrency);
              })
              .catch(() => {
                // Keep current currency if fetch fails
              });
          }
        }
        hasInitialized.current = true;
      }
    } else {
      // Reset flag and currency when modal closes
      hasInitialized.current = false;
      setCurrentCurrencyCode(currencyCode); // Reset to default currency
    }
  }, [isOpen, isEditMode, currencyCode, reset, fetchInvoiceOptions]);

  // Reset body scroll-lock on unmount in case modal was open
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  // ✅ PERFORMANCE: Handle AppSelect changes (client, invoice, paymentMethod)
  // Only sync to parent on change, not on every keystroke
  const handleSelectChange = useCallback((name, value) => {
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
    
    // ✅ PERFORMANCE: Only sync critical dropdowns to parent immediately
    if (parentHandleInputChange && (name === 'client' || name === 'invoice' || name === 'paymentMethod')) {
      parentHandleInputChange(name, value);
    }
    
    // Jika client dipilih, fetch invoice options dan reset currency ke default form.
    // fetchInvoiceOptions is now also called by AppAsyncSelect's onItemSelect
    // (handleClientItemSelect) with the full item object — kept here too so
    // programmatic/RHF-driven client changes (e.g. edit-mode reset) still trigger it.
    if (name === 'client') {
      fetchInvoiceOptions(value);
      setCurrentCurrencyCode(currencyCode);
      // Reset invoice jika client berubah
      setValue('invoice', '');
    }
    
    // ✅ MULTI-CURRENCY: Update currency when invoice is selected (takes priority over company currency)
    if (name === 'invoice' && value) {
      // Try to get currency from invoice options first (faster)
      const selectedInvoice = invoiceOptions.find(inv => inv.value === value);
      if (selectedInvoice?.currencyCode) {
        setCurrentCurrencyCode(selectedInvoice.currencyCode);
      } else {
        // Fallback: Fetch invoice details to get currency
        api.get(`/crm/invoices/${value}`)
          .then(invoiceRes => {
            const invoiceCurrency = invoiceRes.data?.displayCurrencyCode || 
                                   invoiceRes.data?.currencyCode || 
                                   'IDR';
            setCurrentCurrencyCode(invoiceCurrency);
          })
          .catch(() => {
            // Keep current currency if fetch fails
          });
      }
    }
  }, [setValue, parentHandleInputChange, fetchInvoiceOptions, invoiceOptions, currencyCode]);

  // ✅ PERFORMANCE: Handle text input changes - only update local state, sync on blur
  const handleTextInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
    // Don't sync to parent on every keystroke - only on blur
  }, [setValue]);

  // ✅ PERFORMANCE: Sync to parent on blur (not on every keystroke)
  const handleTextInputBlur = useCallback((e) => {
    const { name, value } = e.target;
    if (parentHandleInputChange) {
      parentHandleInputChange(name, value);
    }
  }, [parentHandleInputChange]);

  // ✅ PERFORMANCE: Handle currency input change - only update local state
  const handleCurrencyChange = useCallback((name, value) => {
    setValue(name, value || '', { shouldValidate: false });
    // Don't sync to parent on every keystroke - only on blur
  }, [setValue]);

  const handleClose = () => {
    
    
    // Clear form data when closing
    reset({
      client: '',
      invoice: '',
      paymentMethod: '',
      paymentDate: new Date().toISOString().split('T')[0],
      amount: '',
      note: '',
      documents: []
    });
    setDocuments([]);
    setInvoiceOptions([]);
    // ✅ MULTI-CURRENCY: Reset currency to default when closing
    setCurrentCurrencyCode(currencyCode);
    if (onClose) onClose();
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 3000);
  };

  const onDrop = useCallback((acceptedFiles) => {
    // Only take the first file if multiple files are dropped
    const file = acceptedFiles[0];
    if (file) {
      const fileData = {
        name: file.name,
        size: `${(file.size / 1024).toFixed(1)} KB`,
        url: URL.createObjectURL(file),
        file
      };
      setDocuments([fileData]);
      // Clear document error when file is uploaded
      setError('documents', { type: 'manual', message: '' }, { shouldFocus: false });
      if (parentHandleInputChange) {
        parentHandleInputChange('file', file);
      }
    }
  }, [parentHandleInputChange, setError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    }
  });

  const validateForm = (data) => {
    const errors = {};
    if (!data.client) {
      errors.client = 'Client is required';
    }
    if (!data.invoice) {
      errors.invoice = 'Invoice is required';
    }
    if (!data.paymentMethod) {
      errors.paymentMethod = 'Payment method is required';
    }
    if (!data.paymentDate) {
      errors.paymentDate = 'Payment date is required';
    }
    if (!data.amount || parseFloat(data.amount) <= 0) {
      errors.amount = 'Amount is required and must be greater than 0';
    }
    return errors;
  };

  // ✅ PERFORMANCE: Handle form submit - sync all data to parent before submitting
  const onSubmit = useCallback(async (data) => {
    if (isSubmitting) {
      return;
    }

    const validationErrors = validateForm(data);
    
    // Documents are optional, no validation needed
    
    if (Object.keys(validationErrors).length > 0) {
      // Set errors in form state using react-hook-form's setError
      Object.keys(validationErrors).forEach(key => {
        setError(key, {
          type: 'manual',
          message: validationErrors[key]
        });
      });
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) || 
                          (firstErrorField === 'documents' ? document.querySelector('.dropzone') : null);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      setIsSubmitting(true);

      // Sync all form data to parent before submit
      if (parentHandleInputChange) {
        Object.keys(data).forEach(key => {
          if (data[key] !== undefined && data[key] !== null) {
            parentHandleInputChange(key, data[key]);
          }
        });
        // Also sync documents - only sync new files, not existing ones
        if (documents.length > 0) {
          const newFile = documents.find(doc => doc.file && !doc.isExisting);
          if (newFile) {
            parentHandleInputChange('file', newFile.file);
          } else {
            // If only existing files, set file to null (existing files are already on server)
            parentHandleInputChange('file', null);
          }
        }
      }
      
      // Use parent's form submission handler
      if (parentHandleFormSubmit) {
        // Create a synthetic event for compatibility
        const syntheticEvent = {
          preventDefault: () => {},
          target: {
            ...data,
            documents: documents
          }
        };
        const result = parentHandleFormSubmit(syntheticEvent);
        
        // Handle both promise and non-promise returns
        if (result && typeof result.then === 'function') {
          await result;
        }

        // Reset form after successful submission
        reset({
          client: '',
          invoice: '',
          paymentMethod: '',
          paymentDate: new Date().toISOString().split('T')[0],
          amount: '',
          note: '',
          documents: []
        });
        setDocuments([]);
        handleClose();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save payment';
      showAlert('danger', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, documents, parentHandleInputChange, parentHandleFormSubmit, reset, setError, handleClose]);

  return (
    <>
      <style>{`
        #modal-payment .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-payment .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-payment .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-payment .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-payment .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-payment .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`}
        id="modal-payment"
        style={{ display: isOpen ? 'block' : 'none' }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isEditMode ? "Edit Payment" : "Create Payment"}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isSubmitting}
              ></button>
            </div>

            {alert && (
              <div className="modal-body pb-0">
                <Alert
                  type={alert.type}
                  message={alert.message}
                  onClose={() => setAlert(null)}
                />
              </div>
            )}

            <form onSubmit={async (e) => {
              e.preventDefault();
              
              // Try react-hook-form's handleSubmit first
              const result = handleSubmit(
                onSubmit,
                (errors) => {
                  // If handleSubmit fails, try calling onSubmit directly
                  const formValues = getValues();
                  onSubmit(formValues);
                }
              )(e);
              
              // If handleSubmit returns undefined (validation failed), 
              // the error handler above will handle it
              if (result === undefined) {
                // Error handler should have been called
              }
            }}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  Payment Details
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Controller
                      name="client"
                      control={control}
                      render={({ field }) => (
                        <AppAsyncSelect
                          name="client"
                          label="Client"
                          value={field.value}
                          onChange={handleSelectChange}
                          loadUrl="/v2/crm/companies/options"
                          placeholder="Search client..."
                          required={true}
                          error={formErrors?.client || errors.client?.message}
                          disabled={disableClientSelect || isSubmitting}
                          valueField="value"
                          labelField="label"
                          searchField="label,name"
                          initialValue={clientInitialValue}
                        />
                      )}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Controller
                      name="invoice"
                      control={control}
                      render={({ field }) => (
                        <AppSelect
                          name="invoice"
                          label="Invoice"
                          value={field.value}
                          onChange={handleSelectChange}
                          options={invoiceOptions}
                          placeholder={isLoadingInvoices ? "Loading invoices..." : "Select an invoice"}
                          required={true}
                          error={formErrors?.invoice || errors.invoice?.message}
                          disabled={disableInvoiceSelect || !watchedClient || isLoadingInvoices || isSubmitting}
                          valueField="value"
                          labelField="label"
                          searchField="label,name"
                        />
                      )}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Controller
                      name="paymentMethod"
                      control={control}
                      render={({ field }) => (
                        <AppSelect
                          name="paymentMethod"
                          label="Payment Method"
                          value={field.value}
                          onChange={handleSelectChange}
                          options={paymentMethodOptions}
                          placeholder="Select payment method"
                          required={true}
                          error={formErrors?.paymentMethod || errors.paymentMethod?.message}
                          disabled={isSubmitting}
                          valueField="id"
                          labelField="name"
                          searchField="name"
                        />
                      )}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Controller
                      name="paymentDate"
                      control={control}
                      render={({ field }) => (
                        <>
                          <label className="form-label required">Payment Date</label>
                          <input
                            type="date"
                            {...field}
                            className={`form-control ${formErrors?.paymentDate || errors.paymentDate ? 'is-invalid' : ''}`}
                            disabled={isSubmitting}
                            onBlur={(e) => {
                              field.onBlur();
                              handleTextInputBlur(e);
                            }}
                          />
                          {(formErrors?.paymentDate || errors.paymentDate) && (
                            <div className="invalid-feedback">
                              {formErrors?.paymentDate || errors.paymentDate?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <Controller
                      name="amount"
                      control={control}
                      render={({ field }) => (
                        <>
                          <label className="form-label required">Amount</label>
                          <CurrencyInput
                            className={`form-control ${formErrors?.amount || errors.amount ? 'is-invalid' : ''}`}
                            name={field.name}
                            value={field.value}
                            disabled={isSubmitting}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleCurrencyChange('amount', value);
                            }}
                            onBlur={(e) => {
                              field.onBlur();
                              handleTextInputBlur({ target: { name: 'amount', value: field.value } });
                            }}
                            placeholder="0"
                            decimalsLimit={0}
                            prefix={getCurrencyPrefix(currentCurrencyCode)}
                            allowNegativeValue={false}
                          />
                          {(formErrors?.amount || errors.amount) && (
                            <div className="invalid-feedback">
                              {formErrors?.amount || errors.amount?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <Controller
                      name="note"
                      control={control}
                      render={({ field }) => (
                        <>
                          <label className="form-label">Note</label>
                          <textarea
                            {...field}
                            className="form-control"
                            rows="3"
                            placeholder="Enter payment note"
                            disabled={isSubmitting}
                            onBlur={(e) => {
                              field.onBlur();
                              handleTextInputBlur(e);
                            }}
                          />
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className="inv-section-label mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Document
                </div>
                <div className="mb-3 mt-1">
                  <div className="text-muted small">Upload PDF, JPG, JPEG, or PNG file (optional)</div>
                </div>
                <div
                  {...getRootProps()}
                  className={`dropzone ${isDragActive ? 'active' : ''} ${errors.documents ? 'border-danger' : ''}`}
                  style={{
                    border: `2px dashed ${errors.documents ? '#dc3545' : '#ccc'}`,
                    borderRadius: '4px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    backgroundColor: isDragActive ? '#f8f9fa' : 'white',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  <input {...getInputProps()} disabled={isSubmitting} />
                  {documents.length > 0 ? (
                    <div className="uploaded-files">
                      {documents.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="card mb-2">
                          <div className="card-body p-2">
                            <div className="d-flex align-items-center">
                              {file.name.toLowerCase().endsWith('.pdf') ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-type-pdf text-danger me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                                  <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
                                  <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
                                  <path d="M17 18h2" />
                                  <path d="M20 15h-3v6" />
                                  <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-photo me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M15 8h.01" />
                                  <path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3v-12z" />
                                  <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" />
                                  <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />
                                </svg>
                              )}
                              <div className="file-info flex-grow-1">
                                <div className="text-truncate" style={{ fontSize: '14px', maxWidth: "300px" }}>
                                  {file.name}
                                  {file.isExisting && <span className="badge bg-blue-lt ms-2">Existing</span>}
                                </div>
                                <small className="text-muted">
                                  {file.isExisting ? 'Already uploaded' : file.size}
                                </small>
                              </div>
                              {file.isExisting && file.url && (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-icon btn-ghost-primary btn-sm me-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                                    <path d="M7 11l5 5l5 -5" />
                                    <path d="M12 4l0 12" />
                                  </svg>
                                </a>
                              )}
                              <button
                                type="button"
                                className="btn btn-icon btn-ghost-danger btn-sm"
                                disabled={isSubmitting}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDocuments([]);
                                  // Clear any document errors when document is removed (since it's optional)
                                  setError('documents', { type: 'manual', message: '' }, { shouldFocus: false });
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M18 6l-12 12" />
                                  <path d="M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="text-center mt-2">
                        <small className="text-muted">Drop file or click to select (PDF, JPG, JPEG, PNG)</small>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-cloud-upload mb-2" width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1" />
                        <path d="M9 15l3 -3l3 3" />
                        <path d="M12 12l0 9" />
                      </svg>
                      <small className="text-center text-muted">
                        {isDragActive ? "Drop file here..." : "Drop file here or click to select"}
                      </small>
                      <small className="text-muted mt-1">PDF, JPG, JPEG, PNG files only</small>
                    </div>
                  )}
                </div>
                {errors.documents && (
                  <div className="invalid-feedback d-block mt-2">{errors.documents.message}</div>
                )}
              </div>

              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {isSubmitting ? 'Saving payment…' : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-inv-save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Saving…
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
});

PaymentForm.displayName = 'PaymentForm';

export default PaymentForm; 